import constants from '@/Constants';
import StatusTag from '@/screens/monitoring/Checklists/components/Board/components/StatusTag';
import { getSubdomain } from '@/utils/getSubdomain';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiImage } from 'react-icons/bi';

import { useEventSummary } from '../../hooks/useEventSummary';
import { useInteractions } from '../../hooks/useInteractions';

const createExport = async (
  eventId: string,
  itemId: string,
): Promise<ExportResponse> => {
  try {
    const subdomainFromHost = getSubdomain();
    const url = constants.REPORTS_API_URL || '';
    const parsedUrl = url
      .toLowerCase()
      .replace('{{workspace}}', subdomainFromHost);

    const response = await axios.post(
      `${parsedUrl}external_exports`,
      {
        parentable_type: 'events',
        export_type: 'zip',
        options: {
          objectId: eventId,
        },
        item_id: itemId,
      },
      {
        headers: {
          Authorization: constants.REPORTS_API_AUTHORIZATION_TOKEN,
        },
      },
    );
    return response.data as ExportResponse;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao criar export:', error);
    throw error;
  }
};

interface PollExportStatusParams {
  token: string;
  interval?: number;
  toast?: ReturnType<typeof useToast>;
}

interface ExportResponse {
  token: string;
  status: 'pending' | 'processing' | 'done' | 'failure';
  downloadFileUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

async function pollExportStatus(
  params: PollExportStatusParams,
): Promise<ExportResponse> {
  const { token, interval = 2000, toast } = params;
  const allowedStatuses = ['pending', 'processing'];

  const checkStatus = async (): Promise<ExportResponse> => {
    try {
      const subdomainFromHost = getSubdomain();
      const url = constants.REPORTS_API_URL || '';
      const parsedUrl = url
        .toLowerCase()
        .replace('{{workspace}}', subdomainFromHost);

      const response = await axios.get(
        `${parsedUrl}external_export?token=${token}`,
        {
          headers: {
            Authorization: constants.REPORTS_API_AUTHORIZATION_TOKEN,
          },
        },
      );
      const data = response.data as ExportResponse;

      if (data.status === 'done' && data.downloadFileUrl) {
        window.open(data.downloadFileUrl, '_blank');

        toast?.({
          title: 'Download concluído!',
          description: 'O arquivo está sendo baixado.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return data;
      }

      if (data.status === 'failure') {
        toast?.({
          title: 'Erro no processamento',
          description: 'Não foi possível gerar o arquivo. Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw new Error('Falha no processamento do export');
      }

      if (allowedStatuses.includes(data.status)) {
        await new Promise((resolve) => setTimeout(resolve, interval));
        return checkStatus();
      }

      toast?.({
        title: 'Status inesperado',
        description: `O servidor retornou um status inesperado: ${data.status}`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      throw new Error(`Status inesperado: ${data.status}`);
    } catch (err) {
      toast?.({
        title: 'Erro na comunicação',
        description: 'Não foi possível verificar o status do export.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw err;
    }
  };

  return checkStatus();
}

export const Header: React.FC<{
  standaloneMode?: boolean;
  onClose?: () => void;
}> = () => {
  const { t } = useTranslation();
  const { event } = useEventSummary();
  const { selectedItemId } = useInteractions();
  const toast = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const parsedFinishedAt = React.useMemo(
    () => (event?.finishedAt ? new Date(event?.finishedAt) : null),
    [event?.finishedAt],
  );

  const handleExportZip = async (
    eventId: string,
    itemId: string,
  ): Promise<void> => {
    if (!eventId || !itemId) {
      toast({
        title: t('common.error'),
        description: t('common.incomplete_export_data'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      toast({
        title: t('common.processing'),
        description: t('common.starting_photo_export'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      const exportData = await createExport(eventId, itemId);

      if (!exportData || !exportData.token) {
        throw new Error(t('common.invalid_export_data'));
      }

      if (exportData.status === 'done' && exportData.downloadFileUrl) {
        window.open(exportData.downloadFileUrl, '_blank');
        toast({
          title: t('common.download_completed'),
          description: t('common.file_downloading'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      await pollExportStatus({
        token: exportData.token,
        toast,
      });
    } catch (error) {
      toast({
        title: t('common.export_error'),
        description: t('common.export_completion_error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      w="full"
      minH={{ base: '32', lg: '24' }}
      bg="white"
      borderBottomWidth="thin"
      alignItems="center"
      justifyContent="flex-start"
      display="flex"
      boxShadow="sm"
      gap="4"
    >
      <Flex
        alignItems={{ base: 'flex-start', lg: 'center' }}
        p="4"
        flex="1"
        h="full"
        flexDir={{ base: 'column', lg: 'row' }}
        justifyContent="space-between"
      >
        <VStack alignItems="flex-start" spacing="2">
          <HStack alignItems="center" ms={{ base: '-2', lg: '0' }}>
            <Avatar
              display={{ base: 'none', lg: 'inherit' }}
              borderWidth="3px"
              borderColor="white"
              boxShadow="lg"
              mt="-1.5"
              name={event?.authorName}
              translate="no"
            />
            <Box w="full">
              <Heading
                wordBreak="break-word"
                display="inline-block"
                verticalAlign="middle"
                lineHeight="0.9"
                me="2"
                size="lg"
                translate="no"
              >
                {`${event?.parentableName}`}
              </Heading>
              <Box my="1" display="inline-block">
                <StatusTag status={event?.status || 'pending'} />
              </Box>
            </Box>
          </HStack>
          <Flex
            h="min-content"
            justifyContent="center"
            flexDirection={{ base: 'column', lg: 'row' }}
            gap={{ base: '4', lg: '2' }}
          >
            <Text fontWeight="bold" color="mutedText" translate="no">
              {`${event?.authorName} ${
                event?.userProfileName ? `/ ${event?.userProfileName}` : ''
              } / ${
                parsedFinishedAt ? moment(parsedFinishedAt).format('LLLL') : ''
              }`}
            </Text>
          </Flex>
        </VStack>

        <ButtonGroup
          mt={{ base: '4', lg: '0' }}
          size="md"
          w={{ base: '91.5%', lg: 'fit-content' }}
        >
          <Button
            flex={{ base: 1, lg: 'unset' }}
            onClick={() =>
              handleExportZip(event?.id || '', selectedItemId || '')
            }
            isLoading={isLoading}
            colorScheme="primary"
            leftIcon={<BiImage fontSize="22px" />}
            translate="no"
          >
            {t('common.download_photos')}
          </Button>
        </ButtonGroup>
      </Flex>
    </Box>
  );
};
